/**
 * The Rotom Phone's Map app (plan §9.11): a town map of the Nexus, with the district the player stands in
 * lit live from the audio `context` — the same frame that picks the music, so the map and the soundtrack
 * can never disagree about where somebody is.
 *
 * THE PICTURE is the hub the mod ships, seen from above (`assets/nexus-town-map.png`, rendered from the
 * structure pieces by `scripts/islands/spawn/town_map.py`), and everything on it is drawn in BLOCK UNITS:
 * the SVG's viewBox is the layout's bounds, so a district box or a landmark from `layout.json` lands on its
 * own blocks with no projection to get wrong.
 *
 * A LIVE MAP LATER. A deployment that runs BlueMap (or any web map) sets `VITE_LIVE_MAP_URL` at build time and
 * the app embeds it, keeping the town map one tap away; unset — the default — there is only the town map.
 */
import { useState } from 'react';
import { Link } from 'react-router';

import type { AudioCatalog } from '@dimensionscore/shared';

import townMap from '../assets/nexus-town-map.png';
import { useAudioCompanion } from '../audio/AudioCompanionProvider';
import { plainAreaName } from '../audio/parts';
import { NEXUS_BOUNDS, NEXUS_DIMENSION, NEXUS_DISTRICTS, NEXUS_LANDMARKS, boxCentre, boxRect, districtById, type District } from './nexusLayout';

/** A live web map of the server (BlueMap, squaremap, …) to embed instead; empty means none. */
export const LIVE_MAP_URL: string = ((import.meta.env['VITE_LIVE_MAP_URL'] as string | undefined) ?? '').trim();

/**
 * Landmarks named BELOW their marker rather than above it. The Trainer Center stands 23 blocks east of
 * the Mart on the same row, and their names are wider than that at the map's type size: above both
 * markers they print over each other.
 */
const LABEL_BELOW: ReadonlySet<string> = new Set(['trainer_center']);

/** What the map calls a district: the catalog's own name for its area when it has one, else the shipped name. */
export function districtName(district: District, catalog: AudioCatalog | null): string {
  const area = catalog?.areas.find((candidate) => candidate.trigger.kind === 'district' && candidate.trigger.id === district.id);
  const name = area === undefined ? '' : plainAreaName(area.displayName);
  return name === '' ? district.name : name;
}

export function MapApp(): React.JSX.Element {
  const { state } = useAudioCompanion();
  const live = LIVE_MAP_URL !== '';
  const [view, setView] = useState<'live' | 'town'>(live ? 'live' : 'town');
  const context = state.phase === 'ready' ? state.context : null;
  const where = context?.online === true ? context.context : null;
  const inNexus = where?.dimension === NEXUS_DIMENSION;
  const here = inNexus ? districtById(where?.district) : null;

  let chip: React.ReactNode;
  if (state.phase !== 'ready') {
    chip = (
      <>
        Connect your phone to see where you are — <Link to="/audio">open Music</Link>.
      </>
    );
  } else if (where === null) {
    chip = 'Not in game right now.';
  } else if (!inNexus) {
    chip = 'You are away from the Nexus.';
  } else if (here === null) {
    chip = 'You are in the Nexus, between districts.';
  } else {
    chip = (
      <>
        You are in <b>{districtName(here, state.catalog)}</b>.
      </>
    );
  }

  const { minX, minZ, maxX, maxZ } = NEXUS_BOUNDS;
  const width = maxX - minX + 1;
  const height = maxZ - minZ + 1;
  return (
    <div className="ph-map" data-testid="map" data-district={here?.id ?? ''} data-view={view}>
      <p className="ph-map-here" data-testid="map-here" role="status">
        {chip}
      </p>
      {live && (
        <div className="ph-tabs" role="tablist" aria-label="Map">
          <button type="button" role="tab" aria-selected={view === 'live'} className="ph-tab" onClick={() => setView('live')}>
            Live map
          </button>
          <button type="button" role="tab" aria-selected={view === 'town'} className="ph-tab" onClick={() => setView('town')}>
            Town map
          </button>
        </div>
      )}
      {view === 'live' ? (
        <iframe className="ph-livemap dc-frame" src={LIVE_MAP_URL} title="Live map of the server" data-testid="map-live" loading="lazy" referrerPolicy="no-referrer" />
      ) : (
        <div className="ph-townmap dc-frame">
          <svg viewBox={`${minX} ${minZ} ${width} ${height}`} role="img" aria-labelledby="ph-map-title" data-testid="map-town">
            <title id="ph-map-title">The Nexus from above, north up{here === null ? '' : `, with ${districtName(here, state.catalog)} lit`}</title>
            <image href={townMap} x={minX} y={minZ} width={width} height={height} preserveAspectRatio="none" className="ph-map-image" />
            {NEXUS_DISTRICTS.map((district) => {
              const rect = boxRect(district);
              const lit = here?.id === district.id;
              return (
                <g key={district.id} className={lit ? 'ph-district ph-district-lit' : 'ph-district'} data-testid={`map-district-${district.id}`} data-lit={lit ? 'yes' : 'no'}>
                  <rect {...rect} />
                </g>
              );
            })}
            {NEXUS_LANDMARKS.map((landmark) => (
              <g key={landmark.id} className="ph-landmark">
                <rect x={landmark.x - 1.5} y={landmark.z - 1.5} width={3} height={3} />
                <text x={landmark.x} y={LABEL_BELOW.has(landmark.id) ? landmark.z + 7.5 : landmark.z - 3.5}>
                  {landmark.name}
                </text>
              </g>
            ))}
            {NEXUS_DISTRICTS.map((district) => {
              const centre = boxCentre(district);
              const lit = here?.id === district.id;
              return (
                <text key={district.id} x={centre.x} y={centre.y + (district.id === 'gateway' ? 14 : 0)} className={lit ? 'ph-district-name ph-district-name-lit' : 'ph-district-name'}>
                  {districtName(district, state.catalog)}
                </text>
              );
            })}
          </svg>
        </div>
      )}
      <ul className="ph-map-legend" aria-label="Districts of the Nexus">
        {NEXUS_DISTRICTS.map((district) => (
          <li key={district.id} data-lit={here?.id === district.id ? 'yes' : 'no'}>
            <span className="ph-legend-swatch" aria-hidden="true" />
            {districtName(district, state.catalog)}
            {here?.id === district.id && <span className="dc-visually-hidden"> — you are here</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}
